import { skipToken } from '@reduxjs/toolkit/query/react';

import { config, isFetchError } from '@grafana/runtime';
import { type Folder, useGetFolderQuery } from 'app/api/clients/folder/v1beta1';
import { type RepositoryView, useGetFrontendSettingsQuery } from 'app/api/clients/provisioning/v0alpha1';

import { type RepoType } from '../Wizard/types';
import { getManagerIdentity, isManagedByRepository } from '../utils/managedResource';
import { getIsReadOnlyRepo } from '../utils/repository';

interface GetResourceRepositoryArgs {
  name?: string; // the repository name
  folderName?: string; // folder we are targeting
  skipQuery?: boolean;
  includeInstance?: boolean;
}

export enum RepoViewStatus {
  Disabled = 'disabled',
  Loading = 'loading',
  Ready = 'ready',
  Error = 'error',
  Orphaned = 'orphaned',
}

interface RepositoryViewData {
  repository?: RepositoryView;
  repoType?: RepoType;
  folder?: Folder;
  status: RepoViewStatus;
  error?: unknown;
  orphanedRepoName?: string; // Only present when status is RepoViewStatus.Orphaned
  isLoading?: boolean; // TODO: status now contains loading state, this can be removed
  isInstanceManaged: boolean;
  isReadOnlyRepo: boolean;
  /**
   * True when loading has settled and no repository could be resolved.
   * Consumers gating forms (e.g. ProvisionedFormGate) should use this
   * instead of re-deriving it from isLoading/repository.
   */
  isMissingRepo: boolean;
}

// This is safe to call as a viewer (you do not need full access to the Repository configs)
export const useGetResourceRepositoryView = (args: GetResourceRepositoryArgs): RepositoryViewData => {
  const data = useResourceRepositoryViewData(args);
  return {
    ...data,
    isMissingRepo: !data.isLoading && !data.repository,
  };
};

const useResourceRepositoryViewData = ({
  name,
  folderName,
  skipQuery,
  includeInstance,
}: GetResourceRepositoryArgs): Omit<RepositoryViewData, 'isMissingRepo'> => {
  const provisioningEnabled = config.featureToggles.provisioning;
  // Skip when caller has no target. This query is shared across many
  // components, so a failing fetch would cycle all of them through retries.
  // `includeInstance` overrides the skip for root-level instance lookups.
  const shouldSkipSettings = !provisioningEnabled || skipQuery || (!name && !folderName && !includeInstance);
  const settingsQueryArg = shouldSkipSettings ? skipToken : undefined;

  const {
    data: settingsData,
    isLoading: isSettingsLoading,
    error: settingsError,
  } = useGetFrontendSettingsQuery(settingsQueryArg);

  const skipFolderQuery = !folderName || !provisioningEnabled || skipQuery;
  const {
    data: folder,
    isLoading: isFolderLoading,
    error: folderError,
  } = useGetFolderQuery(skipFolderQuery ? skipToken : { name: folderName });

  if (!provisioningEnabled) {
    return {
      isLoading: false,
      isInstanceManaged: false,
      isReadOnlyRepo: false,
      status: RepoViewStatus.Disabled,
    };
  }

  if (isSettingsLoading || isFolderLoading) {
    return {
      isLoading: true,
      isInstanceManaged: false,
      isReadOnlyRepo: false,
      status: RepoViewStatus.Loading,
    };
  }

  if (settingsError || folderError) {
    // 403 = caller lacks provisioning.settings:read (e.g. folder Admin with no basic
    // role). If the target folder's own annotations show it is not repo-managed,
    // provisioning is irrelevant to this user — treat as "not provisioned" instead of
    // failing closed and blocking unrelated flows like dashboard import.
    // Repo-annotated folders and name-based lookups stay fail-closed: git-sync flows
    // cannot proceed without the settings data anyway.
    if (
      isFetchError(settingsError) &&
      settingsError.status === 403 &&
      !name &&
      !folderError &&
      !(folder && isManagedByRepository(folder))
    ) {
      return { folder, isInstanceManaged: false, isReadOnlyRepo: false, status: RepoViewStatus.Ready };
    }
    return {
      isLoading: false,
      isInstanceManaged: false,
      isReadOnlyRepo: false,
      status: RepoViewStatus.Error,
      error: settingsError || folderError,
    };
  }

  const items = settingsData?.items ?? [];

  // Check for orphaned resource first: name specified but no matching repo
  if (name) {
    const repository = items.find((repo) => repo.name === name);
    const instanceRepo = items.find((repo) => repo.target === 'instance');
    if (repository) {
      return {
        repository,
        folder,
        isInstanceManaged: Boolean(instanceRepo),
        isReadOnlyRepo: getIsReadOnlyRepo(repository),
        status: RepoViewStatus.Ready,
      };
    }

    // When name specified but no matching repository found = orphaned resource
    return {
      folder,
      isInstanceManaged: Boolean(instanceRepo),
      isReadOnlyRepo: false,
      status: RepoViewStatus.Orphaned,
      orphanedRepoName: name,
    };
  }

  if (!items.length) {
    return { folder, isInstanceManaged: false, isReadOnlyRepo: false, status: RepoViewStatus.Ready };
  }

  const instanceRepo = items.find((repo) => repo.target === 'instance');
  const isInstanceManaged = Boolean(instanceRepo);

  // Find the matching folder repository
  if (folderName) {
    // For root values it will be the same
    let repository = items.find((repo) => repo.name === folderName);
    if (repository) {
      return {
        repository,
        folder,
        isInstanceManaged,
        isReadOnlyRepo: getIsReadOnlyRepo(repository),
        status: RepoViewStatus.Ready,
      };
    }

    // For nested folders we need to see what the folder thinks.
    // Only treat as repo-managed if the manager kind is explicitly 'repo' —
    // folders managed by plugins, terraform, kubectl, etc. should not be matched against provisioning repos.
    const annotatedFolderName = folder ? getManagerIdentity(folder) : undefined;
    if (annotatedFolderName && folder && isManagedByRepository(folder)) {
      repository = items.find((repo) => repo.name === annotatedFolderName);
      if (repository) {
        return {
          repository,
          folder,
          isInstanceManaged,
          isReadOnlyRepo: getIsReadOnlyRepo(repository),
          status: RepoViewStatus.Ready,
        };
      }

      // Folder has a manager identity annotation but the repo no longer exists = orphaned
      return {
        folder,
        isInstanceManaged,
        isReadOnlyRepo: false,
        status: RepoViewStatus.Orphaned,
        orphanedRepoName: annotatedFolderName,
      };
    }
  }

  return {
    repository: instanceRepo,
    folder,
    isInstanceManaged,
    isReadOnlyRepo: getIsReadOnlyRepo(instanceRepo),
    repoType: instanceRepo?.type,
    status: RepoViewStatus.Ready,
  };
};
